export function isOneOf<T>(value: T, ...list: T[]): boolean {
	if (isNil(value)) {
		return false;
	}
	return list?.includes(value);
}

export function isNil<T>(value: T): boolean {
	return value === undefined || value === null
}

export function isNumeric(value: string) {
	return /^-?\d+(\.\d+)?$/.test(value.trim()) ? parseFloat(value) : undefined;
}

export function isEmpty<T>(value: T): boolean {
	return isNil(value) || (typeof value === 'string' && value.trim().length === 0);
}


export function copyValue(property: string, from: any, to: any) {
	let prop: string;
	const props: string[] = property.split('.');
	while (props?.length > 1) {
		prop = props.shift()!;
		if (!isNil(from)) {
			from = from[prop];
		}
		if (!isNil(to)) {
			to = to[prop] ?? (to[prop] = (isNil(from) ? undefined : new from.constructor()));
		}
	}
	prop = props.shift()!;
	if (!isNil(to)) {
		to[prop] = from?.[prop];
	}
}

export function setValue(property: string, value: any, to: any) {
	let prop: string;
	const props: string[] = property.split('.');
	while (props?.length > 1) {
		prop = props.shift()!;
		if (isNil(to[prop])) {
			to[prop] = props[0] === '0' ? [] : {};
		}
		to = to[prop];
	}
	prop = props.shift()!;
	if (Array.isArray(to)) {
		if (value === undefined || value === null) {
			to.splice(parseInt(prop!, 10), 1);
		} else {
			to.splice(parseInt(prop!, 10), 1, value);
		}
	} else {
		to[prop] = value;
	}
}

export function getValue(property: string, from: any): any {
	let prop: string;
	const props: string[] = property.split('.');
	while (props?.length > 1) {
		prop = props.shift()!;
		if (isNil(from[prop])) {
			return from[prop];
		}
		from = from[prop];
	}
	prop = props.shift()!;
	return from[prop];
}

export function nameOf<T>(property: keyof T): keyof T {
	return property;
}

export function isEffectivelyEqual(...instances: Object[]) {
	return isEqual(instances, {
		ignoreUndefined: true,
		ignoreNull: true,
		ignoreEmptyArray: true,
		ignoreEmptyObject: true,
		ignoreFalseValue: true,
		ignoreEmptyString: true,
		ignoreArrayOrder: false,
		ignoreType: false,
		ignoreObjectConstructors: true,
	});
}

export function isEqual(instances: any[], options: EqualityOptions): boolean {
	function equalNullOrUndefined(a: any, b: any, v: undefined | null, options: EqualityOptions): boolean {
		if (a === v) {
			if (b !== v) {
				if (Array.isArray(b)) {
					if (!options.ignoreEmptyArray || b.length !== 0) {
						return false;
					}
				} else if (b instanceof Date) {
					return false;
				} else if (typeof b === 'object') {
					if (b === null) {
						if (!options.ignoreNull) {
							return false;
						}
					} else if (!options.ignoreEmptyObject || Object.keys(b).length) {
						return false;
					}
				} else if (b === false) {
					if (!options.ignoreFalseValue) {
						return false;
					}
				} else if (b === '') {
					if (!options.ignoreEmptyString) {
						return false;
					}
				} else if (b == v) {
					return (!options.ignoreUndefined || !options.ignoreNull) ? b === v : true;
				} else {
					return false;
				}
			}
		}
		return true;
	}


	if (!instances?.length || instances?.length < 2) {
		return true;
	}
	let a = instances.pop();
	let b = instances[0];
	// undefined || null
	if (isNil(b) && !isNil(a)) {
		const tmp = a;
		a = b;
		b = tmp;
	}
	if (isNil(a)) {
		if (!equalNullOrUndefined(a, b, a === null ? null : undefined, options)) {
			return false;
		}
	}

	// arrays
	else if (Array.isArray(a)) {
		if (!Array.isArray(b) || a.length !== b.length) {
			return false;
		}
		if (!options.ignoreArrayOrder) {
			for (let i = 0; i < a.length; i++) {
				if (!isEqual([a[i], b[i]], options)) {
					return false;
				}
			}
		} else {
			for (let i = 0; i < a.length; i++) {
				if (!b.some(item => isEqual([item, a[i]], options))) {
					return false;
				}
			}
		}
	}

	// Date
	else if (a instanceof Date) {
		if (!(b instanceof Date) || (a as Date).getTime() !== (b as Date).getTime()) {
			return false;
		}
	}

	// simple types
	else if (typeof a !== 'object') {
		if (options.ignoreType) {
			if (a != b) {
				return false;
			}
		} else if (a !== b) {
			return false;
		}
	}

	// Object
	else {
		if (a.constructor !== b.constructor && !options.ignoreObjectConstructors) {
			return false;
		}
		let aKeys = Object.keys(a);
		let bKeys = new Set(Object.keys(b));
		let ak: any;
		let bk: any;
		for (let k = 0; k < 2; k++) {
			for (const key of aKeys) {
				ak = a[key];
				bk = b[key];
				if (!bKeys.has(key)) {
					if (ak === undefined) {
						if (!options?.ignoreUndefined) {
							return false;
						}
					} else if (ak === null) {
						if (!options?.ignoreNull) {
							return false;
						}
					} else if (ak === false) {
						if (!options?.ignoreFalseValue) {
							return false;
						}
					} else if (ak === '') {
						if (!options?.ignoreEmptyString) {
							return false;
						}
					} else if (!isEqual([ak, bk], options)) {
						return false;
					}
				} else if (!isEqual([ak, bk], options)) {
					return false;
				}
				bKeys.delete(key);
			}
			// once again the other way round with the remaining properties of b
			let tmp = a;
			a = b;
			b = tmp;
			tmp = aKeys;
			aKeys = [...bKeys];
			bKeys = new Set(tmp as []);
		}
	}

	if (instances.length > 1) {
		return isEqual(instances, options);
	}

	return true;
}

export type EqualityOptions = {
	ignoreUndefined?: boolean;
	ignoreNull?: boolean;
	ignoreEmptyObject?: boolean;
	ignoreEmptyArray?: boolean;
	ignoreFalseValue?: boolean;
	ignoreEmptyString?: boolean;
	ignoreArrayOrder?: boolean;
	ignoreObjectConstructors?: boolean;
	ignoreType?: boolean;
}
